import './KbContentDiff.css'
import { diffChars, diffLines, type Change } from 'diff'
import { cn } from '@/utilities/misc'

type Props = {
  oldText: string
  newText: string
  className?: string
}

/**
 * Line-level diff, with character-level refinement for adjacent
 * removed/added hunks that still share most of their content
 * (e.g. a single word change on a line).
 */
function hybridDiff(oldText: string, newText: string): Change[] {
  const lineChanges = diffLines(oldText, newText)
  const result: Change[] = []

  for (let i = 0; i < lineChanges.length; i++) {
    const curr = lineChanges[i]!
    const next = lineChanges[i + 1]

    if (curr.removed && next?.added && shouldRefine(curr.value, next.value)) {
      result.push(...diffChars(curr.value, next.value))
      i++
      continue
    }

    result.push(curr)
  }

  return result
}

/** Prefer char-level when unchanged content outweighs the edit. */
function shouldRefine(removed: string, added: string): boolean {
  if (!removed || !added) return false

  const charChanges = diffChars(removed, added)
  let unchanged = 0
  let changed = 0
  for (const part of charChanges) {
    if (part.added || part.removed)
      changed += part.value.length
    else
      unchanged += part.value.length
  }

  return unchanged >= changed
}

export function KbContentDiff({ oldText, newText, className }: Props) {
  const changes = hybridDiff(oldText, newText)
  const hasChanges = changes.some(c => c.added || c.removed)

  if (!hasChanges) {
    return (
      <div className={cn('KbContentDiff', 'Empty', className)}>
        No differences
      </div>
    )
  }

  return (
    <pre className={cn('KbContentDiff', className)}>
      {changes.map((part, i) => {
        if (part.added) {
          return (
            <span key={i} className='Added'>
              {part.value}
            </span>
          )
        }
        if (part.removed) {
          return (
            <span key={i} className='Removed'>
              {part.value}
            </span>
          )
        }
        return (
          <span key={i} className='Unchanged'>
            {part.value}
          </span>
        )
      })}
    </pre>
  )
}
